/**
 * TodosPage.jsx: To Do (/todos). Everyone's own to-do list.
 *
 * One flat list in the order its owner drags it into (grip at the right of each
 * open row; new to-dos land on top; a due date is only a label, overdue in red),
 * done ones collapsed below. Every row has the $ mark (faint grey, gold when marked). Quick add makes a private to-do for me; Details opens the full
 * dialog. Admins also get the Team view (everyone's assigned to-dos, never private
 * ones, with an assignee filter, in due-date order, no grips), can assign to-dos
 * to people, and see the portal's suggestions above their own list.
 *
 * Gate: EmployeeRoute in App.jsx. The server scopes every answer; admin here is
 * only for showing the admin controls.
 *
 * URL state:
 *   ?view=mine|team   (team: admins only)
 *   ?assignee=<id>    Team view filter
 *   ?todo=<id>        opens that to-do (email and calendar links, Home rows); pushed
 *                     with state so the phone's back gesture closes the dialog. The
 *                     dialog opens at once with a spinner; its form shows only data
 *                     fetched since this page mounted, never a cached copy from an
 *                     earlier visit (its notes may be old).
 * The New to-do dialog (Details, New) pushes a history entry too (state only, the
 * URL stays the same), so back closes it rather than leaving the page.
 *
 * Contract: docs/agent-notes/todo-page.md (section 5.1).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import {
  Box, Typography, Button, CircularProgress, Alert, TextField, ToggleButtonGroup,
  ToggleButton, Collapse, Paper,
} from '@mui/material';
import {
  Checklist as TodoIcon,
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useRBAC } from '../hooks/useRBAC';
import { useToast } from '../components/layout/Toast';
import {
  useTodos, useTodo, useTodoSuggestions, useCreateTodo, useUpdateTodo, useSetTodoMoney, useReorderTodos,
  useAcceptSuggestion, useDismissSuggestion, newTodoId,
} from '../hooks/useTodos';
import { sortTodos, sortMine, applyOrder } from '../lib/todoText';
import TodoRow, { SortableTodoRow } from '../components/todos/TodoRow';
import TodoDialog from '../components/todos/TodoDialog';
import AssigneePicker from '../components/todos/AssigneePicker';
import SuggestionsPanel from '../components/todos/SuggestionsPanel';

const btn = { textTransform: 'none', minHeight: 44 };

function Centered({ children }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>{children}</Box>;
}

const DRAG_HELP = {
  draggable: 'To move a to-do, press space on its grip, move it with the arrow keys, then press space to drop it or escape to cancel.',
};

export default function TodosPage() {
  const { profile } = useAuth();
  const { isAdmin } = useRBAC();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const meId = profile?.id;

  const setParams = useCallback((fn, opts = { replace: true }) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      fn(next);
      return next;
    }, opts);
  }, [setSearchParams]);

  // ── View ──
  const view = isAdmin && searchParams.get('view') === 'team' ? 'team' : 'mine';
  const assignee = view === 'team' ? searchParams.get('assignee') || '' : '';
  const setView = (v) => setParams((p) => {
    if (v === 'team') p.set('view', 'team');
    else { p.delete('view'); p.delete('assignee'); }
  });
  const setAssignee = (id) => setParams((p) => (id ? p.set('assignee', id) : p.delete('assignee')));

  // ── Data ──
  // Always revalidated (mount, view switch, focus): a ?todo= dialog waits for it.
  const listQ = useTodos({ view, assignee }, { staleTime: 0 });
  const suggestionsQ = useTodoSuggestions({ enabled: isAdmin && view === 'mine' });
  const create = useCreateTodo();
  const update = useUpdateTodo();
  const setMoney = useSetTodoMoney();
  const accept = useAcceptSuggestion();
  const dismiss = useDismissSuggestion();
  const { save: saveOrder, pendingOrder } = useReorderTodos({
    onFailed: () => toast('Could not save the order. Try again.', 'error'),
  });
  const today = listQ.data?.today;
  // Mine: the order I dragged it into (sortKey, as the server sorts it), so an untick lands
  // back in its place at once; while a drop is saving, that drop's order over whatever
  // the list holds (useReorderTodos). Team: by due date.
  const sorted = useMemo(() => {
    if (view === 'team') return sortTodos(listQ.data?.items);
    return pendingOrder ? applyOrder(listQ.data?.items, pendingOrder) : sortMine(listQ.data?.items);
  }, [view, listQ.data, pendingOrder]);
  const open = useMemo(() => sorted.filter((t) => !t.done), [sorted]);
  const done = useMemo(() => sorted.filter((t) => t.done), [sorted]);
  const openIds = useMemo(() => open.map((t) => t.id), [open]);
  const [showDone, setShowDone] = useState(false);

  // ── Drag to reorder (Mine, open rows, by the grip only) ──
  // The pointer sensor also takes touches on the grip (touch-action: none there);
  // the touch sensor covers browsers without pointer events.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = openIds.indexOf(active.id);
    const to = openIds.indexOf(over.id);
    if (from < 0 || to < 0) return;
    saveOrder(arrayMove(openIds, from, to));
  };
  // Screen readers hear positions, not ids.
  const announcements = useMemo(() => {
    const at = (id) => `position ${openIds.indexOf(id) + 1} of ${openIds.length}`;
    return {
      onDragStart: ({ active }) => `Picked up the to-do at ${at(active.id)}.`,
      onDragOver: ({ over }) => (over ? `Moved to ${at(over.id)}.` : 'Not over the list.'),
      onDragEnd: ({ over }) => (over ? `Dropped at ${at(over.id)}.` : 'Dropped. Nothing moved.'),
      onDragCancel: () => 'Cancelled. The to-do is back where it was.',
    };
  }, [openIds]);

  // ── ?todo=<id> ──
  // A save or delete can drop the to-do from this list before the param is gone
  // (reassigned off Mine, deleted). `closedKey` stops that from reopening it or
  // reading as "no longer on your list" while the dialog closes. It marks the
  // history entry that was closed, not the to-do: back or Forward onto any entry
  // (even one with the same ?todo=) opens it again.
  const todoParam = searchParams.get('todo');
  const [closedKey, setClosedKey] = useState(null);
  useEffect(() => {
    setClosedKey((k) => (k === location.key ? k : null));
  }, [location.key]);
  const activeId = todoParam && location.key !== closedKey ? todoParam : null;
  const listFresh = listQ.isFetchedAfterMount;
  const inList = activeId && listFresh ? sorted.find((t) => t.id === activeId) || null : null;
  const lookup = useTodo(activeId, { enabled: listFresh && !inList, staleTime: 0 });
  const freshTodo = inList
    || (activeId && lookup.isFetchedAfterMount && lookup.data?.id === activeId ? lookup.data : null);
  // While it is open, a refetch can drop it from the list (reassigned elsewhere): keep the
  // copy on screen until the lookup answers, so the form (and its unsaved edits) stays.
  const lastShown = useRef(null);
  if (!activeId) lastShown.current = null;
  else if (freshTodo) lastShown.current = freshTodo;
  const dialogTodo = freshTodo || (lastShown.current?.id === activeId ? lastShown.current : null);
  useEffect(() => {
    if (!activeId || !lookup.isError || !lookup.isFetchedAfterMount) return;
    toast(lookup.error?.status === 404 ? 'This to-do is no longer on your list.' : 'Could not open that to-do. Try again.', 'error');
    setParams((p) => p.delete('todo'));
  }, [activeId, lookup.isError, lookup.isFetchedAfterMount, lookup.error, toast, setParams]);

  const openTodo = useCallback((t) => {
    // Already this entry's to-do: no second ?todo= entry (back would land on the first).
    if (t.id === todoParam) {
      setClosedKey(null);
      return;
    }
    setParams((p) => p.set('todo', t.id), { replace: false, state: { todoDialog: true } });
  }, [todoParam, setParams]);
  // The dialog's onClose is the one from the render where Save or Delete was tapped. It acts
  // only while that history entry is still the open one: a save or delete that lands after
  // back, after another to-do opened, or after the page was left must not navigate again.
  const todoEntry = useRef(null);
  useLayoutEffect(() => {
    todoEntry.current = activeId ? location.key : null;
    return () => { todoEntry.current = null; };
  });
  const closeTodo = useCallback(() => {
    if (todoEntry.current !== location.key) return;
    todoEntry.current = null;
    setClosedKey(location.key);
    if (location.state?.todoDialog) navigate(-1);
    else setParams((p) => p.delete('todo'));
  }, [location.key, location.state, navigate, setParams]);

  // ── New to-do (Details / New) ──
  // Open while its own history entry is current (matched by key, so an entry left
  // from an earlier draft never reopens anything); back or Forward closes or reopens it.
  // Like closeTodo, a save acts only on its own draft while that entry is still open: one that
  // lands after back (or after another draft opened) must not go back again or close the new one.
  const [draft, setDraft] = useState(null); // null | { key, initial, fromQuick }
  const draftOpen = !!draft && location.state?.newTodo === draft.key;
  const draftEntry = useRef(null);
  useLayoutEffect(() => {
    draftEntry.current = draftOpen ? draft.key : null;
    return () => { draftEntry.current = null; };
  });
  const openDraft = (initial, fromQuick = false) => {
    const key = Date.now();
    setDraft({ key, initial, fromQuick });
    setParams(() => {}, { replace: false, state: { newTodo: key } });
  };
  const closeDraft = useCallback(() => {
    if (draftEntry.current !== draft?.key) return;
    draftEntry.current = null;
    navigate(-1);
  }, [draft, navigate]);

  // ── Quick add ──
  // Errors come back per call through mutateAsync: with mutate(vars, { onError }), a
  // second add while the first is in flight would drop the first one's error.
  // Details leaves the text in the field until its dialog saves the to-do.
  const [quick, setQuick] = useState('');
  const quickRef = useRef(null);
  const quickNow = useRef('');
  quickNow.current = quick;
  const quickAdd = () => {
    const title = quick.trim();
    if (!title) return;
    quickNow.current = '';
    setQuick('');
    quickRef.current?.focus();
    create.mutateAsync({ id: newTodoId(), title: title.slice(0, 300) }).catch((err) => {
      // Put the text back if the field is free; else say which one didn't save.
      if (!quickNow.current.trim()) {
        quickNow.current = title;
        setQuick(title);
        toast(err?.message || 'Could not add it. Try again.', 'error');
      } else {
        toast(`Could not add "${title.length > 60 ? `${title.slice(0, 60)}...` : title}". Try again.`, 'error');
      }
    });
  };

  const toggleDone = useCallback((t) => {
    update.mutateAsync({ id: t.id, done: !t.done })
      .catch((err) => toast(err?.message || 'Could not save it. Try again.', 'error'));
  }, [update, toast]);

  // The $ mark: shows at once, and only this row's mark goes back if the save fails.
  const toggleMoney = useCallback((t) => {
    setMoney.mutateAsync({ id: t.id, money: !t.money })
      .catch(() => toast('Could not save it. Try again.', 'error'));
  }, [setMoney, toast]);

  // ── Suggestions ──
  const [busyKey, setBusyKey] = useState(null);
  const handleSuggestion = async (s, action) => {
    setBusyKey(s.key);
    try {
      if (action === 'accept') {
        await accept.mutateAsync({ key: s.key, kind: s.kind });
        toast('Added to your to-dos, due today.');
      } else {
        await dismiss.mutateAsync({ key: s.key, kind: s.kind });
      }
    } catch (err) {
      // 404 suggestion_gone and 409 suggestion_taken explain themselves; the list refreshes either way.
      toast(err?.message || 'Could not do that. Try again.', 'error');
    } finally {
      setBusyKey(null);
    }
  };

  const listError = listQ.isError && !listQ.data;

  return (
    <Box data-track-area="todos" sx={{ maxWidth: 900, mx: 'auto', pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
          <TodoIcon sx={{ color: '#38BDF8' }} />
          <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>To Do</Typography>
        </Box>
        {isAdmin && (
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => { if (v) setView(v); }}>
            <ToggleButton data-track="todos.view.mine" value="mine" sx={{ ...btn, px: 2 }}>Mine</ToggleButton>
            <ToggleButton data-track="todos.view.team" value="team" sx={{ ...btn, px: 2 }}>Team</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {view === 'team' ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <AssigneePicker
            everyone
            value={assignee}
            onChange={setAssignee}
            meId={meId}
            label="Assigned to"
            trackKey="todos.filter.assignee"
            sx={{ flex: { xs: '1 1 100%', sm: '0 1 280px' } }}
          />
          <Button
            data-track="todos.new"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDraft({ assigneeId: assignee })}
            sx={{ ...btn, ml: { sm: 'auto' } }}
          >
            New to-do
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            inputRef={quickRef}
            fullWidth
            size="small"
            placeholder="Add a to-do"
            value={quick}
            onChange={(e) => setQuick(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                quickAdd();
              }
            }}
            slotProps={{ htmlInput: { maxLength: 300, enterKeyHint: 'done', 'aria-label': 'Add a to-do' } }}
            sx={{ '& .MuiInputBase-root': { minHeight: 44 } }}
          />
          <Button
            data-track="todos.quick.details"
            variant="outlined"
            onClick={() => openDraft({ title: quick.trim() }, true)}
            sx={{ ...btn, flexShrink: 0 }}
          >
            Details
          </Button>
        </Box>
      )}

      {isAdmin && view === 'mine' && (
        <SuggestionsPanel
          query={suggestionsQ}
          busyKey={busyKey}
          onAccept={(s) => handleSuggestion(s, 'accept')}
          onDismiss={(s) => handleSuggestion(s, 'dismiss')}
        />
      )}

      {listError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => listQ.refetch()} sx={{ textTransform: 'none' }}>Try again</Button>}
        >
          Could not load to-dos.
        </Alert>
      )}

      {listQ.isPending ? <Centered><CircularProgress size={28} /></Centered> : !listError && (
        <>
          <Paper variant="outlined" sx={{ p: 0.5 }}>
            {view === 'mine' ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragEnd}
                accessibility={{ announcements, screenReaderInstructions: DRAG_HELP }}
              >
                <SortableContext items={openIds} strategy={verticalListSortingStrategy}>
                  {open.map((t) => (
                    <SortableTodoRow
                      key={t.id}
                      todo={t}
                      today={today}
                      meId={meId}
                      onToggle={toggleDone}
                      onMoney={toggleMoney}
                      onOpen={openTodo}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : open.map((t) => (
              <TodoRow
                key={t.id}
                todo={t}
                today={today}
                meId={meId}
                showAssignee
                onToggle={toggleDone}
                onMoney={toggleMoney}
                onOpen={openTodo}
              />
            ))}
            {open.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                {view === 'team' ? 'Nothing assigned.' : 'Nothing to do. Add one above.'}
              </Typography>
            )}
          </Paper>

          {done.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box
                data-track="todos.doneToggle"
                onClick={() => setShowDone((v) => !v)}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 44, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                {showDone ? <ArrowDownIcon sx={{ fontSize: 20 }} /> : <ArrowRightIcon sx={{ fontSize: 20 }} />}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Done ({done.length})</Typography>
              </Box>
              <Collapse in={showDone}>
                <Paper variant="outlined" sx={{ p: 0.5 }}>
                  {done.map((t) => (
                    <TodoRow
                      key={t.id}
                      todo={t}
                      today={today}
                      meId={meId}
                      showAssignee={view === 'team'}
                      onToggle={toggleDone}
                      onMoney={toggleMoney}
                      onOpen={openTodo}
                    />
                  ))}
                </Paper>
              </Collapse>
            </Box>
          )}
        </>
      )}

      {/* ?todo=<id>: open at once (it also covers the list), the form once its data is fresh */}
      <TodoDialog open={!!activeId} loading={!dialogTodo} todo={dialogTodo} today={today} onClose={closeTodo} />
      {/* New to-do */}
      <TodoDialog
        open={draftOpen}
        initial={draft?.initial}
        today={today}
        teamView={view === 'team'}
        onSaved={() => {
          // Clear the field only if it still holds the text Details took (it is editable after back).
          if (draft?.fromQuick && quickNow.current.trim() === draft.initial.title) setQuick('');
          setDraft((d) => (d?.key === draft?.key ? null : d));
        }}
        onClose={closeDraft}
      />
    </Box>
  );
}
